import { Injectable, inject } from '@angular/core';
import { Dialog } from '@angular/cdk/dialog';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import type { AppearanceValue } from './appearance-editor';
import {
  AppearanceEditDialogComponent,
  type AppearanceEditDialogData,
  type AppearanceEditDialogResult,
} from './appearance-edit-dialog.component';

export interface AppearanceEditConfig {
  /** Dialog title (already translated). */
  title: string;
  /** Default Material icon name when no icon is set. */
  defaultIcon: string;
  /** Current appearance value from the form. */
  value: AppearanceValue;
  /** Labels for the icon, color, and image URL fields (already translated). */
  iconLabel: string;
  colorLabel: string;
  imageUrlLabel: string;
}

/**
 * Opens the appearance-edit dialog with standard configuration.
 *
 * All five consumer sites share identical dialog config (dimensions, panel/backdrop
 * classes) and result handling (filter save, extract value). This service
 * centralises that boilerplate into a single call.
 *
 * @example
 * ```ts
 * this.appearanceEditService
 *   .open({
 *     title: this.translate.instant('settings.teams.sections.appearance'),
 *     defaultIcon: 'groups',
 *     value: this.form.controls.appearance.value ?? { icon: null, color: null, imageUrl: null },
 *     iconLabel: this.translate.instant('settings.teams.fields.icon'),
 *     colorLabel: this.translate.instant('settings.teams.fields.color'),
 *     imageUrlLabel: this.translate.instant('settings.teams.fields.imageUrl'),
 *   })
 *   .subscribe(value => {
 *     this.form.patchValue({ appearance: value });
 *     this.form.markAsDirty();
 *   });
 * ```
 */
@Injectable({ providedIn: 'root' })
export class AppearanceEditService {
  private readonly dialog = inject(Dialog);

  /**
   * Opens the appearance edit dialog and returns an Observable that emits
   * the saved `AppearanceValue` or completes without emission if cancelled.
   */
  open(config: AppearanceEditConfig): Observable<AppearanceValue> {
    const data: AppearanceEditDialogData = {
      title: config.title,
      defaultIcon: config.defaultIcon,
      value: config.value,
      iconLabel: config.iconLabel,
      colorLabel: config.colorLabel,
      imageUrlLabel: config.imageUrlLabel,
    };

    const dialogRef = this.dialog.open<AppearanceEditDialogResult, AppearanceEditDialogData>(AppearanceEditDialogComponent, {
      width: 'min(96vw, 1040px)',
      maxWidth: '96vw',
      height: 'min(92dvh, 980px)',
      maxHeight: '92dvh',
      panelClass: 'ai-chat-dialog-panel',
      backdropClass: 'ai-chat-dialog-backdrop',
      data,
    });

    return dialogRef.closed.pipe(
      filter((result): result is AppearanceEditDialogResult & { value: AppearanceValue } => result?.action === 'save' && !!result.value),
      map(result => result.value)
    );
  }
}
